// pages/LiteStarDashboard.tsx
import React, { useState } from "react";
import { NanoGasCanvas } from "./NanoGasCanvas";
import { LATEST_THEME_CONFIG, LiteStarTheme } from "./types";

export const LiteStarDashboard: React.FC = () => {
  const [theme, setTheme] = useState<LiteStarTheme>("nanolite-secure");
  const [gasLevel, setGasLevel] = useState<number>(0);
  const [isCardOpen, setIsCardOpen] = useState<boolean>(false);

  const currentStyle = LATEST_THEME_CONFIG[theme];
  const isLeaking = theme === "gas-leakage";

  // 가스 슬라이더 조절 시 80%를 넘어가면 비상 경고 모드 트리거
  const handleGasSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    setGasLevel(value);
    if (value >= 60) {
      setTheme("gas-leakage");
    } else {
      setTheme("nanolite-secure");
    }
  };

  return (
    <div className={`relative min-h-screen w-full overflow-hidden p-6 ${currentStyle.wrapper} ${currentStyle.font}`}>
      {/* 백그라운드 나노가스 이펙트 레이어 */}
      <NanoGasCanvas isActive={isLeaking} gasLevel={gasLevel} />

      {/* --- 상단 실시간 경고 및 상태 네비게이션 배너 --- */}
      <header className="relative z-10 max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between border-b border-slate-700/40 pb-4 mb-10">
        <div>
          <h1 className="text-2xl font-black tracking-wider flex items-center gap-2">
            {isLeaking ? "☣️ Nanolite_Corp_Hazard" : "🏢 NanoLite Corp."}
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            {isLeaking 
              ? "CRITICAL: MAIN LABORATORY GAS LEAKAGE DETECTED" 
              : "INTERNAL STORAGE & DATA TERMINAL"}
          </p>
        </div>

        {/* 상단 기믹: 나노가스 농도 수동 제어판 */}
        <div className="mt-4 md:mt-0 flex items-center gap-4 bg-black/40 border border-slate-800 p-3 rounded-xl">
          <span className="text-xs font-bold text-slate-400">GAS LEAK LEVEL:</span>
          <input
            type="range"
            min="0"
            max="100"
            value={gasLevel}
            onChange={handleGasSliderChange}
            className="accent-emerald-500 h-1 w-32 rounded-lg appearance-none bg-slate-700 cursor-pointer"
          />
          <span className={`text-xs font-mono font-bold w-8 ${isLeaking ? 'text-emerald-400' : 'text-blue-400'}`}>
            {gasLevel}%
          </span>
        </div>
      </header>

      {/* --- 메인 인터랙티브 허브 레이아웃 --- */}
      <main className="relative z-10 max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
        
        {/* 왼쪽 섹션: 세계관 시스템 환경 상태 표시창 */}
        <div className={`p-6 rounded-3xl ${currentStyle.card}`}>
          <div className={`inline-block px-3 py-1 rounded-full border text-[10px] font-bold tracking-widest ${currentStyle.badge} mb-4`}>
            SYSTEM STATUS
          </div>
          <h3 className="text-lg font-bold mb-4">📍 버뮤다 기지 모니터링</h3>
          
          <div className="space-y-3 text-xs font-mono">
            <div className="flex justify-between border-b border-slate-800 pb-2">
              <span className="text-slate-400">CURRENT_LOCATION:</span>
              <span className="text-white font-bold">BERMUDA TRIANGLE</span>
            </div>
            <div className="flex justify-between border-b border-slate-800 pb-2">
              <span className="text-slate-400">CORE_PROJECT:</span>
              <span className="text-blue-400 font-bold tracking-widest">LITE-STAR</span>
            </div>
            <div className="flex justify-between pb-2">
              <span className="text-slate-400">INFECTED_GAS_TYPE:</span>
              <span className="text-emerald-400 font-bold">NANO_GAS_v2.4</span>
            </div>
          </div>

          {isLeaking && (
            <div className="mt-6 p-3 bg-emerald-950/30 border border-emerald-500/20 rounded-xl text-[11px] text-emerald-400/90 leading-relaxed animate-pulse">
              ⚠️ <strong>시스템 경고:</strong> 나노가스 유출로 인해 버뮤다 기지 내부 인원의 유전자 변형 데이터가 감지되었습니다. 격리 프로토콜을 준비하십시오.
            </div>
          )}
        </div>

        {/* 가운데 섹션: 3D Flip 캐릭터 실험 데이터 카드[cite: 2] */}
        <div 
          onClick={() => setIsCardOpen(!isCardOpen)} 
          className="group relative w-full aspect-[4/5] cursor-pointer"
          style={{ perspective: "2000px" }}
        >
          <div className={`relative w-full h-full transition-all duration-[800ms] transform-style-3d shadow-2xl rounded-[2rem] ${
            isCardOpen ? "rotate-y-180" : "hover:scale-[1.01]"
          }`}>
            
            {/* [카드 앞면]: NanoLite 기밀 문서 아웃라인[cite: 2] */}
            <div className={`absolute inset-0 backface-hidden rounded-[2rem] p-8 flex flex-col justify-between overflow-hidden ${currentStyle.card}`}>
              <div className="flex justify-between text-[10px] opacity-50 font-mono">
                <span>DOC_NO: #NL-1205</span>
                <span className={isLeaking ? 'text-red-500 font-bold' : ''}>
                  {isLeaking ? "⚠️ LEAKED" : "🔒 SECURE"}
                </span>
              </div>
              
              <div className="text-center relative py-6">
                <span className="text-7xl block transition-transform duration-500 group-hover:scale-110">
                  {isLeaking ? "🧬" : "👤"}
                </span>
                <h4 className="text-xl font-black mt-4 tracking-widest">
                  {isLeaking ? "SUBJECT: LITER" : "EXPERIMENTAL OBJECT"}
                </h4>
              </div>

              <div className="w-full space-y-2 text-center text-[10px] text-slate-400 tracking-wider">
                <div className="h-[1px] bg-slate-700 w-12 mx-auto mb-2" />
                <p>{isLeaking ? "CLICK TO ANALYSIS CAPABILITY" : "TOUCH TO DECRYPT FILE"}</p>
              </div>
            </div>

            {/* [카드 뒷면]: 나노가스로 각성한 Liter의 스탯 정보[cite: 2] */}
            <div className={`absolute inset-0 backface-hidden rotate-y-180 rounded-[2rem] p-8 flex flex-col justify-between bg-zinc-950 text-slate-200 border ${
              isLeaking ? 'border-emerald-500/60' : 'border-blue-500/40'
            }`}>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-[10px] bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded border border-emerald-500/20 font-bold">
                    LITER MUTATION LOG
                  </span>
                  <span className="text-xs font-mono font-bold text-slate-400">MUT-099</span>
                </div>
                
                <div className="space-y-2">
                  <h3 className="text-2xl font-black text-white">나노가스 각성체</h3>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    NanoLite 기업이 개발한 고밀도 나노가스에 노출된 후 세포 변형을 일으켜 초인적인 특수 능력군 **'Liter'**로 각성한 상태의 개체 기록 아카이브입니다.
                  </p>
                </div>
              </div>

              <div className="border-t border-slate-800 pt-4 flex items-center justify-between">
                <div>
                  <p className="text-[9px] text-slate-500 uppercase tracking-widest">Origin Terminal</p>
                  <p className="font-mono text-sm text-emerald-400 font-bold">Bermuda_Sector_3</p>
                </div>
                <span className="text-3xl">✨</span>
              </div>
            </div>

          </div>
        </div>

        {/* 오른쪽 섹션: 게임 빌드 데이터 연동 퀵메뉴 */}
        <div className="flex flex-col gap-4 w-full">
          {[
            { title: "Project LiteStar 개요", subtitle: "나노가스 실험 및 프로젝트 총괄 문서", icon: "📑" },
            { title: "Liter 인벤토리 로그", subtitle: "변이 개체들의 능력치 데이터 시트", icon: "⚔️" },
            { title: "보안 시스템 강제 재부팅", subtitle: "가스 배출 장치 및 오염도 초기화", icon: "🔄" }
          ].map((item, idx) => (
            <div
              key={idx}
              onClick={() => idx === 2 && handleGasSliderChange({ target: { value: '0' } } as any)}
              className={`group p-5 rounded-2xl flex items-center justify-between transition-all duration-300 cursor-pointer ${currentStyle.card}`}
            >
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg bg-slate-800 group-hover:scale-105 transition-transform`}>
                  {item.icon}
                </div>
                <div>
                  <h4 className="text-xs font-bold tracking-wide text-white group-hover:text-emerald-400 transition-colors">
                    {item.title}
                  </h4>
                  <p className="text-[10px] text-slate-400 mt-0.5">{item.subtitle}</p>
                </div>
              </div>
              <span className="text-slate-500 group-hover:text-white transition-colors text-xs">→</span>
            </div>
          ))}
        </div>

      </main>

      {/* --- 글로벌 3D 회전 및 펄스 효과용 마법의 CSS 주입 --- */}
      <style>{`
        .transform-style-3d {
          transform-style: preserve-3d;
        }
        .backface-hidden {
          backface-visibility: hidden;
        }
        .rotate-y-180 {
          transform: rotateY(180deg);
        }
        .animate-pulse-slow {
          animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.93; border-color: rgba(16, 185, 129, 0.6); }
        }
      `}</style>
    </div>
  );
};